/**
 * SMS Safety Report Form
 * Guided multi-step safety report triggered from the flight wizard notes step.
 * Pre-populated with trip/flight context. Follows TC CARs 107 / AC 107-002.
 *
 * Demo-ready UI — stores locally, backend wiring in Phase 4.
 */

import { useState } from 'react';
import {
  ShieldExclamationIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// ── Types ────────────────────────────────────────────────────────────────────

type HazardCategory =
  | 'weather'
  | 'mechanical'
  | 'atc'
  | 'ground_ops'
  | 'human_factors'
  | 'bird_strike'
  | 'runway_incursion'
  | 'cabin_safety'
  | 'other';

type Severity = 'negligible' | 'minor' | 'major' | 'hazardous' | 'catastrophic';
type Likelihood = 'improbable' | 'remote' | 'occasional' | 'probable' | 'frequent';

interface SafetyReportData {
  narrative: string;
  hazardCategory: HazardCategory | '';
  severity: Severity | '';
  likelihood: Likelihood | '';
  actionTaken: string;
  recommendedFollowUp: string;
  anonymous: boolean;
}

interface FlightContext {
  date: string;
  tailNumber: string;
  aircraftType: string;
  fromAirport: string;
  toAirport: string;
  pilotName: string;
  qNumber: string;
  tripId?: number;
  flightId?: number;
}

interface Props {
  flightContext: FlightContext;
  onClose: () => void;
  onSubmit: (report: SafetyReportData) => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const HAZARD_CATEGORIES: { value: HazardCategory; label: string; icon: string }[] = [
  { value: 'weather', label: 'Weather', icon: '🌩️' },
  { value: 'mechanical', label: 'Mechanical', icon: '🔧' },
  { value: 'atc', label: 'ATC / Airspace', icon: '📡' },
  { value: 'ground_ops', label: 'Ground Ops', icon: '🛬' },
  { value: 'human_factors', label: 'Human Factors', icon: '🧠' },
  { value: 'bird_strike', label: 'Bird / Wildlife', icon: '🦅' },
  { value: 'runway_incursion', label: 'Runway Incursion', icon: '🚧' },
  { value: 'cabin_safety', label: 'Cabin Safety', icon: '💺' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const SEVERITY_LEVELS: { value: Severity; label: string; color: string }[] = [
  { value: 'negligible', label: 'Negligible', color: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' },
  { value: 'minor', label: 'Minor', color: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
  { value: 'major', label: 'Major', color: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700' },
  { value: 'hazardous', label: 'Hazardous', color: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700' },
  { value: 'catastrophic', label: 'Catastrophic', color: 'bg-red-200 text-red-900 border-red-400 dark:bg-red-900/50 dark:text-red-300 dark:border-red-600' },
];

const LIKELIHOOD_LEVELS: { value: Likelihood; label: string; description: string }[] = [
  { value: 'improbable', label: 'Improbable', description: 'Almost never happens' },
  { value: 'remote', label: 'Remote', description: 'Unlikely but possible' },
  { value: 'occasional', label: 'Occasional', description: 'May happen sometimes' },
  { value: 'probable', label: 'Probable', description: 'Will happen occasionally' },
  { value: 'frequent', label: 'Frequent', description: 'Expected to happen often' },
];

const STEPS = ['Narrative', 'Hazard', 'Risk', 'Actions', 'Review'];

// ── Risk matrix color ────────────────────────────────────────────────────────

function getRiskLevel(severity: Severity | '', likelihood: Likelihood | ''): { label: string; color: string } {
  if (!severity || !likelihood) return { label: '', color: '' };

  const sevIdx = SEVERITY_LEVELS.findIndex(s => s.value === severity);
  const likIdx = LIKELIHOOD_LEVELS.findIndex(l => l.value === likelihood);
  const score = (sevIdx + 1) * (likIdx + 1);

  if (score <= 4) return { label: 'Low', color: 'bg-green-500' };
  if (score <= 9) return { label: 'Medium', color: 'bg-yellow-500' };
  if (score <= 16) return { label: 'High', color: 'bg-orange-500' };
  return { label: 'Critical', color: 'bg-red-500' };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function SafetyReportForm({ flightContext, onClose, onSubmit }: Props) {
  const [step, setStep] = useState(0);
  const [report, setReport] = useState<SafetyReportData>({
    narrative: '',
    hazardCategory: '',
    severity: '',
    likelihood: '',
    actionTaken: '',
    recommendedFollowUp: '',
    anonymous: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (field: keyof SafetyReportData, value: any) => {
    setReport(prev => ({ ...prev, [field]: value }));
  };

  const canAdvance = (): boolean => {
    switch (step) {
      case 0: return report.narrative.trim().length > 10;
      case 1: return report.hazardCategory !== '';
      case 2: return report.severity !== '' && report.likelihood !== '';
      case 3: return report.actionTaken.trim().length > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleSubmit = () => {
    onSubmit(report);
    setSubmitted(true);
  };

  // ── Success screen ──

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Report Filed</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Thank you for contributing to flight safety.
            {report.anonymous && ' Your identity will remain confidential.'}
          </p>
          <p className="text-xs text-muted-foreground/70 mb-6">
            This report will be reviewed by your safety officer as part of the company SMS program.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold"
          >
            Continue Flight Entry
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ──

  const risk = getRiskLevel(report.severity, report.likelihood);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center">
              <ShieldExclamationIcon className="w-6 h-6 text-amber-700 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-amber-900 dark:text-amber-100">Safety Report</h2>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {flightContext.qNumber} &middot; {flightContext.fromAirport} → {flightContext.toAirport} &middot; {flightContext.tailNumber}
              </p>
            </div>
            <button onClick={onClose} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 text-sm font-medium">
              Cancel
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex gap-1.5 mt-3">
            {STEPS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${
                  i < step ? 'bg-amber-500 dark:bg-amber-400'
                    : i === step ? 'bg-amber-400 dark:bg-amber-500'
                    : 'bg-amber-200 dark:bg-amber-800'
                }`} />
                <p className={`text-[10px] mt-1 text-center ${
                  i <= step ? 'text-amber-700 dark:text-amber-400' : 'text-amber-400 dark:text-amber-700'
                }`}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* Step 0: Narrative */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">What happened?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Describe the event in your own words. Include what you observed, when it occurred during the flight, and any contributing factors.
                </p>
              </div>
              <textarea
                rows={8}
                value={report.narrative}
                onChange={(e) => update('narrative', e.target.value)}
                className="w-full p-4 border-2 border-border bg-background text-foreground rounded-xl focus:border-amber-500 focus:outline-none resize-none text-sm leading-relaxed"
                placeholder="During the approach to KIAD, at approximately 3,000 ft AGL, we encountered..."
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-right">
                {report.narrative.length} characters
              </p>
            </div>
          )}

          {/* Step 1: Hazard Category */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Hazard Category</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  What type of hazard best describes this event?
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {HAZARD_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => update('hazardCategory', cat.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                      report.hazardCategory === cat.value
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500 shadow-sm'
                        : 'border-border hover:border-amber-300 dark:hover:border-amber-700'
                    }`}
                  >
                    <span className="text-2xl">{cat.icon}</span>
                    <span className={`text-xs font-medium ${
                      report.hazardCategory === cat.value ? 'text-amber-800 dark:text-amber-300' : 'text-foreground'
                    }`}>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Risk Assessment */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Risk Assessment</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Rate the severity and likelihood of this hazard.
                </p>
              </div>

              {/* Severity */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Severity — What was the worst potential outcome?</label>
                <div className="flex flex-col gap-1.5">
                  {SEVERITY_LEVELS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => update('severity', s.value)}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all text-left ${
                        report.severity === s.value
                          ? s.color + ' border-current shadow-sm'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full ${
                        report.severity === s.value ? 'bg-current' : 'bg-muted'
                      }`} />
                      <span className="text-sm font-medium">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Likelihood */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Likelihood — How likely is this to happen again?</label>
                <div className="flex flex-col gap-1.5">
                  {LIKELIHOOD_LEVELS.map((l) => (
                    <button
                      key={l.value}
                      onClick={() => update('likelihood', l.value)}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
                        report.likelihood === l.value
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500 shadow-sm'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <span className="text-sm font-medium text-foreground">{l.label}</span>
                      <span className="text-xs text-muted-foreground">{l.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Risk level badge */}
              {risk.label && (
                <div className="flex items-center justify-center gap-2 py-3 bg-muted/50 rounded-xl">
                  <span className={`w-3 h-3 rounded-full ${risk.color}`} />
                  <span className="text-sm font-semibold text-foreground">Overall Risk: {risk.label}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Actions */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Actions & Follow-up</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  What did you do in response, and what should be done to prevent recurrence?
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Immediate action taken <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={report.actionTaken}
                  onChange={(e) => update('actionTaken', e.target.value)}
                  className="w-full p-3 border-2 border-border bg-background text-foreground rounded-xl focus:border-amber-500 focus:outline-none resize-none text-sm"
                  placeholder="Executed a go-around and advised ATC of the situation..."
                  autoFocus
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Recommended follow-up <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={report.recommendedFollowUp}
                  onChange={(e) => update('recommendedFollowUp', e.target.value)}
                  className="w-full p-3 border-2 border-border bg-background text-foreground rounded-xl focus:border-amber-500 focus:outline-none resize-none text-sm"
                  placeholder="Suggest reviewing approach procedures for this airport..."
                />
              </div>

              {/* Anonymous toggle */}
              <div className="flex items-center justify-between bg-muted/50 rounded-xl p-4">
                <div>
                  <p className="text-sm font-medium text-foreground">File anonymously</p>
                  <p className="text-xs text-muted-foreground">Your identity will not be attached to this report</p>
                </div>
                <button
                  type="button"
                  onClick={() => update('anonymous', !report.anonymous)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    report.anonymous ? 'bg-amber-500' : 'bg-border'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    report.anonymous ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <ExclamationTriangleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>
                  This is a <strong>non-punitive</strong> safety reporting system per TC CARs 107. Reports are used to improve operational safety, not for disciplinary purposes.
                </span>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Review Report</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Confirm the details below before submitting.
                </p>
              </div>

              {/* Flight context card */}
              <div className="bg-muted/50 rounded-xl p-4 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Flight</span>
                  <span className="font-medium text-foreground">{flightContext.qNumber} — {flightContext.fromAirport} → {flightContext.toAirport}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aircraft</span>
                  <span className="font-medium text-foreground">{flightContext.tailNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium text-foreground">{new Date(flightContext.date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reporter</span>
                  <span className="font-medium text-foreground">{report.anonymous ? 'Anonymous' : flightContext.pilotName}</span>
                </div>
              </div>

              {/* Report summary */}
              <div className="space-y-3">
                <ReviewRow label="Hazard" value={
                  HAZARD_CATEGORIES.find(c => c.value === report.hazardCategory)
                    ? `${HAZARD_CATEGORIES.find(c => c.value === report.hazardCategory)!.icon} ${HAZARD_CATEGORIES.find(c => c.value === report.hazardCategory)!.label}`
                    : '-'
                } />
                <ReviewRow label="Risk Level">
                  {risk.label && (
                    <span className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${risk.color}`} />
                      <span className="font-medium">{risk.label}</span>
                      <span className="text-muted-foreground ml-1">
                        ({SEVERITY_LEVELS.find(s => s.value === report.severity)?.label} / {LIKELIHOOD_LEVELS.find(l => l.value === report.likelihood)?.label})
                      </span>
                    </span>
                  )}
                </ReviewRow>
                <ReviewRow label="Narrative" value={report.narrative} multiline />
                <ReviewRow label="Action Taken" value={report.actionTaken} multiline />
                {report.recommendedFollowUp && (
                  <ReviewRow label="Follow-up" value={report.recommendedFollowUp} multiline />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="border-t border-border px-5 py-4 flex gap-3">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
          ) : (
            <div />
          )}
          <div className="flex-1" />
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <ShieldExclamationIcon className="w-4 h-4" />
              Submit Report
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helper component ─────────────────────────────────────────────────────────

function ReviewRow({ label, value, multiline, children }: { label: string; value?: string; multiline?: boolean; children?: React.ReactNode }) {
  return (
    <div className="border-b border-border pb-2">
      <p className="text-xs font-medium text-muted-foreground mb-0.5">{label}</p>
      {children || (
        multiline
          ? <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{value}</p>
          : <p className="text-sm font-medium text-foreground">{value}</p>
      )}
    </div>
  );
}
